// @ts-strict-ignore
import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import {
  Box,
  Button,
  ButtonVariant,
  Text,
  useSnackBar,
} from '@usealloy/component-library';
import { parse } from 'query-string';
import type { FieldValues } from 'react-hook-form';
import {
  useForm, FormProvider,
} from 'react-hook-form';

import { ajaxFetch } from '~/helpers/api';
import log from '~/helpers/logging';
import { routes } from '~/helpers/routes';
import FormContainer from '../FormContainer';
import { InputFieldType } from '../FormContainer/types';
import { StyledLink } from '../AuthContainerView/styles';
import { StyledDivider, StyledDividerText } from './styles';
import { getTimezoneAuthData, validateVanityUrl } from '../../helpers';
import {
  matchPath, useNavigate, useLocation, NavLink,
} from 'react-router-dom';

const genericSigninErrorMessage = 'Something went wrong while signing in.';

declare const window: Window &
  typeof globalThis & {
  google: {
    accounts: {
      id: {
        initialize: Function;
        renderButton: Function;
      };
    };
  };
};

interface IFormFields {
  email: string;
  password: string;
}

const formDefaultValues = {
  email: '',
  password: '',
};

const actionBtnStyle = {
  flex: 1,
  backgroundColor: '#000',
  color: '#fff',
  marginTop: 5,
  marginBottom: 5,
};

const signInBtnStyle = {
  backgroundColor: '#000',
  color: '#fff',
  marginTop: 5,
  marginBottom: 5,
};

const ssoBtnStyle = {
  flex: 1,
  backgroundColor: '#fff',
  color: '#3c4043',
  borderColor: '#dadce0',
  borderRadius: 'var(--cl-radius-1)',
  fontWeight: 'var(--cl-font-weight-medium)',
};

const TOTP_MFA_AUTH_SNACKBAR = 'Multi-Factor Authentication successfully enabled. For security purposes please log in again.';

const SigninView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const addSnackBar = useSnackBar();

  const [googleOauthClientId, setGoogleOauthClientId] = useState<string>(undefined);
  const [singleTenantSSOUrl, setSingleTenantSSOUrl] = useState<string>(undefined);

  const [ssoLoginUrl, setSsoLoginUrl] = useState<string>(undefined);
  const [enableCredentialLogin, setEnableCredentialLogin] = useState<boolean>(false);
  const [isLoadingSsoMethod, setIsLoadingSsoMethod] = useState<boolean>(false);

  const methods = useForm<IFormFields>({
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  const urlParams = useMemo(() => parse(location.search), [location]);

  useEffect(() => {
    if (
      urlParams?.result === 'logout_success'
      && urlParams?.saml !== 'true'
      && !urlParams?.next
      && !urlParams?.reason
    ) {
      addSnackBar({
        title: 'You have been logged out.',
        type: 'success',
      });
    }
    // value has to be a string bc that's the only primative in session storage
    if (sessionStorage.getItem('oneTimeTotpSuccess') === 'true') {
      sessionStorage.setItem('oneTimeTotpSuccess', 'false');
      addSnackBar({
        title: TOTP_MFA_AUTH_SNACKBAR,
        type: 'success',
      });
    }
  }, [addSnackBar, urlParams]);

  const handleCredentialResponse = useCallback(
    async response => {
      const genericErrorMessage = 'Something went wrong while signing in with Google.';
      try {
        const {
          redirectUrl, isAdmin, result, message,
        } = await ajaxFetch(
          routes.authentication.login.googleOauth,
          {
            method: 'POST',
            body: JSON.stringify({
              idtoken: response.credential,
              next_url: urlParams?.next,
              ...getTimezoneAuthData(),
            }),
            camelCase: true,
            enhancedErrorResponse: true,
          },
        );

        if (result === 'success' && urlParams?.vanity_url) {
          let redirectDestination = redirectUrl || '/evaluations/';
          const vanityUrlRedirect = decodeURIComponent(
            urlParams.vanity_url as string,
          );
          const isValidVanityUrl = validateVanityUrl(urlParams);
          if (isValidVanityUrl) {
            redirectDestination = `${vanityUrlRedirect}${
              isAdmin && !redirectUrl ? '/v3/dashboard/admin/customers/' : redirectDestination
            }`;
          }
          window.location.replace(redirectDestination);
        } else if (result === 'success' && redirectUrl) {
          let redirectDestination = redirectUrl || '/evaluations/';
          if (isAdmin && !redirectUrl) {
            redirectDestination = '/v3/dashboard/admin/customers/';
          }

          window.location.replace(redirectDestination);
        } else if (result === 'success_alloyadmin_created') {
          addSnackBar({
            title: message,
            type: 'success',
          });
        } else {
          throw new Error(genericErrorMessage);
        }
      } catch (err) {
        addSnackBar({
          title: err.message || genericErrorMessage,
          type: 'danger',
        });
        log(
          'error',
          `GET ${routes.authentication.login.googleOauth} exception`,
          {},
          err,
        );
      }
    },
    [addSnackBar, urlParams?.next, urlParams?.vanity_url],
  );

  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.referrerPolicy = 'strict-origin-when-cross-origin';

    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: googleOauthClientId,
        callback: handleCredentialResponse,
      });
      if (googleOauthClientId) {
        window.google.accounts.id.renderButton(
          document.getElementById('google_signin_button'),
          { theme: 'outline', size: 'large', logo_alignment: 'center' },
        );
      }
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [googleOauthClientId, handleCredentialResponse]);

  const fetchGoogleOauthClientId = useCallback(async () => {
    try {
      const { googleOauthClientId: clientId } = await ajaxFetch(
        routes.authentication.login.googleOauth,
        { silent: true },
      );

      if (clientId) {
        setGoogleOauthClientId(clientId);
      }
    } catch (error) {
      addSnackBar({
        title:
          'Something went wrong while loading the authentication module. Please refresh the page and try again.',
        type: 'danger',
      });
      log(
        'error',
        `GET ${routes.authentication.login.googleOauth} exception`,
        {},
        error,
      );
    }
  }, [addSnackBar]);

  useEffect(() => {
    fetchGoogleOauthClientId();
  }, [fetchGoogleOauthClientId]);

  const checkIfSingleTenantSSO = useCallback(async () => {
    try {
      const { redirectUrl } = await ajaxFetch(
        routes.authentication.login.basic,
        { camelCase: true },
      );

      if (redirectUrl) {
        if (urlParams?.saml !== 'true') {
          const isValidVanityUrl = validateVanityUrl(urlParams);
          window.location.replace(isValidVanityUrl ? `${urlParams.vanity_url}${redirectUrl}` : redirectUrl);
        } else {
          setSingleTenantSSOUrl(redirectUrl);
        }
      }
    } catch (error) {
      addSnackBar({
        title:
          'There was a problem redirecting you to the single sign-on provider. Please try again or contact support.',
        type: 'danger',
      });
      log(
        'error',
        `GET ${routes.authentication.login.basic} exception`,
        {},
        error,
      );
    }
  }, [addSnackBar, urlParams?.saml, urlParams?.vanity_url]);

  useEffect(() => {
    const SPA_PREFIX = '/v3/dashboard';

    const isAdminLoginPath = !!matchPath(`${SPA_PREFIX}/login/admin`, location.pathname);

    const isRbacAccessRequest = location.search?.startsWith(
      `?next=${routes.v3.dashboard.admin.customerAccess.list}`,
    );
    if (!isAdminLoginPath && !isRbacAccessRequest) checkIfSingleTenantSSO();
  }, [location, checkIfSingleTenantSSO]);

  const fields = useMemo(() => {
    if (enableCredentialLogin) {
      return [
        {
          type: InputFieldType.EMAIL,
          label: 'Email',
          name: 'email',
        },
        {
          type: InputFieldType.PASSWORD,
          label: 'Password',
          name: 'password',
        },
      ];
    }
    return [
      {
        type: InputFieldType.EMAIL,
        label: 'Email',
        name: 'email',
      },
    ];
  }, [enableCredentialLogin]);

  const handleSigninErrorResponse = responseBody => {
    switch (responseBody?.result) {
      case 'error_account_inactive':
      case 'error':
        addSnackBar({
          title: responseBody?.message || genericSigninErrorMessage,
          type: 'danger',
        });
        break;
      case 'error_password_expired': {
        if (responseBody?.redirectUrl) {
          const tokenString = responseBody.redirectUrl.match(
            /([0-9A-Za-z_-]+\/[0-9A-Za-z]{1,13}-[0-9A-Za-z]{1,40})/g,
          );
          const nextUrlString = responseBody.redirectUrl.match(/\?next=(\/[^&]*)/);

          navigate(
            routes.v3.dashboard.authentication['reset-password'](tokenString?.[0]),
            {
              state: {
                nextUrl: nextUrlString?.[0],
                message: {
                  text: responseBody.message,
                  type: 'danger',
                },
              },
            },
          );
        }
        break;
      }
      case 'error_ip_restricted': {
        if (responseBody?.message) {
          addSnackBar({
            title: responseBody?.message,
            type: 'danger',
          });
        }
        break;
      }
      default: {
        addSnackBar({
          title: genericSigninErrorMessage,
          type: 'danger',
        });
        log(
          'error',
          `GET ${routes.authentication.login.basic} exception`,
          {},
          responseBody,
        );
        break;
      }
    }
  };

  const onLoginSubmit = async (formData: FieldValues) => {
    try {
      const response = await ajaxFetch(routes.authentication.login.basic, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          next_url: urlParams?.next || '',
          ...getTimezoneAuthData(),
        }),
        camelCase: true,
        enhancedErrorResponse: true,
      });

      let tokenString;
      let nextUrlString;

      if (response?.redirectUrl) {
        tokenString = response.redirectUrl.match(
          /([0-9A-Za-z_-]+\/[0-9A-Za-z]{1,13}-[0-9A-Za-z]{1,40})/g,
        );
        nextUrlString = response.redirectUrl.match(/\?next=(\/[^&]*)/);
      }

      switch (response?.result) {
        case 'success': {
          let redirectDestination = response.redirectUrl || '/evaluations/';

          if (
            !response.redirectUrl
            || response.redirectUrl === '/evaluations/'
          ) {
            const data = await ajaxFetch(routes.v3.userContext, {
              camelCase: true,
            });
            if (response.isAdmin) {
              redirectDestination = routes.v3.dashboard.admin.customers.list;
            } else {
              const isJourneyAnalyticsEnabled = data?.featureFlags.journeys;
              const isJourneyViewingPermissionsEnabled = data?.featureFlags
                .journeyViewingPermissions;
              const isJourneyAnalyticsAllowed = data?.permissions['READ:JourneyAnalytics:null'];
              const isJourneyViewingAllowed = data?.permissions['READ:Journey:journey_id'];
              const isCaseManagementAnalyticsAllowed = data?.permissions['READ:CaseManagementAnalytics:null'];

              /**
               * Only redirect to journey analytics dashboard if analytics FF is enabled,
               * analytics are allowed for current agent,
               * and either journey viewing permissions FF is disabled or it's enabled and
               * agent has journey viewing permissions.
               */
              const shouldRedirectToJourneyAnalyticsDashboard = isJourneyAnalyticsEnabled
                && isJourneyAnalyticsAllowed
                && (!isJourneyViewingPermissionsEnabled
                  || (isJourneyViewingPermissionsEnabled
                    && isJourneyViewingAllowed));

              if (
                isCaseManagementAnalyticsAllowed
                || shouldRedirectToJourneyAnalyticsDashboard
              ) {
                redirectDestination = '/v3/dashboard/home/';
              }
            }
          }

          const isValidVanityUrl = validateVanityUrl(urlParams);
          window.location.replace(isValidVanityUrl ? `${urlParams.vanity_url}${redirectDestination}` : redirectDestination);
          break;
        }
        case 'next_mfa_verification': {
          navigate(routes.v3.dashboard.authentication['mfa-verify-code'](tokenString?.[0]), {
            state: { nextUrl: nextUrlString?.[1] },
          });
          break;
        }
        case 'next_mfa_totp_verification': {
          navigate(routes.v3.dashboard.authentication['mfa-totp-verify-code'](tokenString?.[0]), {
            state: { nextUrl: nextUrlString?.[1] },
          });
          break;
        }
        case 'next_mfa_setup': {
          navigate(routes.v3.dashboard.authentication['mfa-setup-verify-phonenumber'](tokenString?.[0]), {
            state: { nextUrl: nextUrlString?.[1] },
          });
          break;
        }
        case 'next_mfa_totp_setup': {
          navigate(routes.v3.dashboard.authentication['mfa-setup-totp'](tokenString?.[0]), {
            state: { nextUrl: nextUrlString?.[1] },
          });
          break;
        }
        default:
          throw new Error(genericSigninErrorMessage);
      }
    } catch (err) {
      const response = await err.response?.json();
      if (response) {
        handleSigninErrorResponse(response);
      } else {
        handleSigninErrorResponse(err);
      }
    }
  };

  const isLoggedOutFromSamlProvider = useMemo(
    () => urlParams?.saml === 'true' && urlParams?.result === 'success',
    [urlParams],
  );

  /**
   *
   * @param formData
   *
   * - If the sso_only is set to true and sso_login_url is returned. This means the customer has SSO setup and only wants to use SSO as a sign-in method
        Redirect them directly to the IDP authentication page

    - If the sso_only is set to false and sso_login_url is returned. This means the customer has SSO setup and wants to have SSO and another sign-in method.
        We show the password input box and the single sign-on button.

    - If the sso_only is set to false and sso_login_url is not returned. This means the customer has not set up the SSO yet.
        Show the password input box.

    -
   */
  const getLoginMethod = async (formData: FieldValues) => {
    const { ssoOnly, ssoLoginUrl: ssoUrl } = await ajaxFetch(
      routes.authentication.login.login_method,
      {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          ...getTimezoneAuthData(),
          vanity_url: urlParams.vanity_url,
        }),
        camelCase: true,
        enhancedErrorResponse: true,
      },
    );

    if (ssoOnly && ssoUrl) {
      setIsLoadingSsoMethod(true);
      window.location.assign(ssoUrl);
    } else if (!ssoOnly && ssoUrl) {
      setSsoLoginUrl(ssoUrl);
      setEnableCredentialLogin(true);
    } else {
      setEnableCredentialLogin(true);
    }
  };

  const renderSPInitiatedView = useCallback(() => (
    <>
      <Text as="h1" size={7} style={{ textAlign: 'center' }}>
        Sign In
      </Text>
      {!isLoadingSsoMethod && (
        <FormProvider {...methods}>
          <FormContainer
            onSubmitActionText={enableCredentialLogin ? 'Sign In' : 'Continue'}
            fields={fields}
            onSubmit={enableCredentialLogin ? onLoginSubmit : getLoginMethod}
            customSubmitButtonStyle={enableCredentialLogin ? signInBtnStyle : actionBtnStyle}
          >
            {enableCredentialLogin && (
              <NavLink to={routes.v3.dashboard.authentication['forgot-password']}>
                <StyledLink forwardedAs="span">
                  Forgot Password
                </StyledLink>
              </NavLink>
            )}
          </FormContainer>
        </FormProvider>
      )}
      <>
        <StyledDivider>
          <StyledDividerText>Or</StyledDividerText>
        </StyledDivider>
        <div id="google_signin_button" />
        {ssoLoginUrl && (
          <Box direction="row">
            <Button
              type="submit"
              style={ssoBtnStyle}
              onClick={() => window.location.assign(ssoLoginUrl)}
            >
              Sign in with SSO
            </Button>
          </Box>
        )}
      </>
    </>
  ), [fields, enableCredentialLogin, ssoLoginUrl, isLoadingSsoMethod]);

  return (
    <Box
      direction="column"
      gap={isLoggedOutFromSamlProvider ? 6 : 4}
      alignItems={isLoggedOutFromSamlProvider ? 'center' : 'stretch'}
      style={{ width: '440px' }}
    >
      {isLoggedOutFromSamlProvider ? (
        <>
          <Text as="h1" size={7} style={{ textAlign: 'center' }}>
            You&apos;ve been logged out.
          </Text>
          {singleTenantSSOUrl && (
            <Button
              variant={ButtonVariant.primary}
              onClick={() => {
                const isValidVanityUrl = validateVanityUrl(urlParams);
                window.location.replace(isValidVanityUrl ? `${urlParams.vanity_url}/${singleTenantSSOUrl}` : singleTenantSSOUrl);
              }}
            >
              Return To SSO Provider
            </Button>
          )}
          <NavLink to={routes.v3.dashboard.authentication.login}>
            <StyledLink forwardedAs="span">Return to sign in</StyledLink>
          </NavLink>
        </>
      ) : (
        <>{renderSPInitiatedView()}</>

      )}
    </Box>
  );
};

export default SigninView;
